import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, FileDown, Plus, Trash2, Save as SaveIcon, MapPin, Zap, Calendar, ArrowRight, FileText, User, Phone } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { API_BASE_URL, PREVIEW_OFFLINE } from '../../config';
import toast from 'react-hot-toast';

interface QuoteInput {
    capacityKwInput: string;
    pricePerKwInput: string; // default "1100"
    horsepowerInput: string; // عدد الأحصنة (HP)
    deviceTypeInput: string; // رأس كهربائي | غطاس
    deviceCountInput: string; // عدد الأجهزة
}

interface ComputedQuote extends QuoteInput {
    category: 'الأفضل' | 'الجيد' | 'الاقتصادي' | 'الأدنى';
    totalBeforeTax: number;
    vat15: number;
    totalWithTax: number;
    deviceType: 'رأس كهربائي' | 'غطاس';
    deviceCount: number;
    capacityKw: number;
    pricePerKw: number;
    horsepower: number;
    voltage: number;
}

interface SavedQuotationSummary {
    id: string;
    quote_number: string;
    quote_date: string;
    customer_name: string;
    location: string | null;
    mobile: string | null;
    created_at: string;
    total_with_tax: number;
}

const categoryByIndex = (i: number) => ['الأفضل', 'الجيد', 'الاقتصادي', 'الأدنى'][i] as ComputedQuote['category'];

const computeQuote = (q: QuoteInput, index: number): ComputedQuote => {
    const capacity = Number(parseFloat(q.capacityKwInput) || 0);
    const price = Number(q.pricePerKwInput === '' ? 1100 : parseFloat(q.pricePerKwInput));
    const horsepower = Number(parseFloat(q.horsepowerInput) || 0);
    const deviceType = (q.deviceTypeInput === 'غطاس') ? 'غطاس' : 'رأس كهربائي';
    const deviceCount = Number(parseFloat(q.deviceCountInput) || 0) || 1;
    const voltage = 380;
    const totalBeforeTax = Math.round(capacity * price);
    const vat15 = Math.round(totalBeforeTax * 0.15);
    const totalWithTax = totalBeforeTax + vat15;
    return {
        category: categoryByIndex(index),
        capacityKw: capacity,
        pricePerKw: price,
        horsepower,
        totalBeforeTax,
        vat15,
        totalWithTax,
        deviceType,
        deviceCount,
        voltage,
        // include raw inputs for convenience
        capacityKwInput: q.capacityKwInput,
        pricePerKwInput: q.pricePerKwInput,
        horsepowerInput: q.horsepowerInput,
        deviceTypeInput: q.deviceTypeInput,
        deviceCountInput: q.deviceCountInput,
    };
};

const ModernCreateQuotationScreen: React.FC = () => {
    const navigate = useNavigate();
    const [customerName, setCustomerName] = useState('');
    const [location, setLocation] = useState('');
    const [mobile, setMobile] = useState('');
    const [quotes, setQuotes] = useState<QuoteInput[]>([{ capacityKwInput: '', pricePerKwInput: '1100', horsepowerInput: '', deviceTypeInput: 'رأس كهربائي', deviceCountInput: '1' }]);
    const [saving, setSaving] = useState(false);
    const [quoteNumber, setQuoteNumber] = useState<string | null>(null);
    const [quoteDate, setQuoteDate] = useState<string | null>(null);
    const [savedQuotations, setSavedQuotations] = useState<SavedQuotationSummary[]>([]);
    const [quotationDetails, setQuotationDetails] = useState<Record<string, { horsepower?: number; device_type?: string; device_count?: number }>>({});

    const addQuote = () => {
        if (quotes.length >= 4) return;
        setQuotes([...quotes, { capacityKwInput: '', pricePerKwInput: '1100', horsepowerInput: '', deviceTypeInput: 'رأس كهربائي', deviceCountInput: '1' }]);
    };

    const removeQuote = (idx: number) => {
        setQuotes(quotes.filter((_, i) => i !== idx));
    };

    const updateQuote = (idx: number, field: keyof QuoteInput, value: string) => {
        const updated = [...quotes];
        if (field === 'capacityKwInput') updated[idx].capacityKwInput = value;
        if (field === 'pricePerKwInput') updated[idx].pricePerKwInput = value;
        if (field === 'horsepowerInput') updated[idx].horsepowerInput = value;
        if (field === 'deviceTypeInput') updated[idx].deviceTypeInput = value;
        if (field === 'deviceCountInput') updated[idx].deviceCountInput = value;
        setQuotes(updated);
    };

    useEffect(() => {
        const fetchSaved = async () => {
            try {
                const canGet = !PREVIEW_OFFLINE;
                if (!canGet) return;
                const res = await fetch(`${API_BASE_URL}/quotations?limit=12`);
                if (!res.ok) return;
                const data = await res.json();
                if (Array.isArray(data)) setSavedQuotations(data as SavedQuotationSummary[]);
                else if (Array.isArray((data as any).items)) setSavedQuotations((data as any).items as SavedQuotationSummary[]);
            } catch { }
        };
        fetchSaved();
    }, []);

    useEffect(() => {
        if (PREVIEW_OFFLINE || savedQuotations.length === 0) return;
        const missing = savedQuotations.filter(q => !quotationDetails[q.id]);
        if (missing.length === 0) return;
        (async () => {
            try {
                const results = await Promise.all(missing.map(async (q) => {
                    const res = await fetch(`${API_BASE_URL}/quotations/${q.id}`);
                    if (!res.ok) return { id: q.id, first: null } as { id: string; first: any };
                    const data = await res.json();
                    const first = Array.isArray(data.items) && data.items.length > 0 ? data.items[0] : null;
                    return { id: q.id, first } as { id: string; first: any };
                }));
                setQuotationDetails(prev => {
                    const next = { ...prev };
                    for (const r of results) {
                        if (r.first) {
                            next[r.id] = {
                                horsepower: Number(r.first.horsepower || 0),
                                device_type: r.first.device_type,
                                device_count: r.first.device_count,
                            };
                        } else {
                            next[r.id] = {};
                        }
                    }
                    return next;
                });
            } catch { }
        })();
    }, [savedQuotations]);

    const computedQuotes: ComputedQuote[] = quotes.map((q, i) => computeQuote(q, i));

    const deleteQuotation = async (id: string) => {
        try {
            const ok = confirm('هل تريد حذف العرض؟');
            if (!ok) return;
            const res = await fetch(`${API_BASE_URL}/quotations/${id}`, { method: 'DELETE' });
            if (!res.ok) throw new Error(await res.text());
            setSavedQuotations(prev => prev.filter(q => q.id !== id));
            toast.success('تم حذف العرض بنجاح');
        } catch (e) {
            toast.error('تعذر حذف العرض');
        }
    };

    const generatePDF = async (data: { quoteNumber: string; date: string; quotes: ComputedQuote[] }) => {
        const doc = new jsPDF('p', 'pt', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.left = '-10000px';
        container.style.top = '0';
        container.style.width = '794px';
        container.style.height = '1123px';
        container.style.padding = '40px';
        container.style.direction = 'rtl';
        container.style.fontFamily = 'Cairo, Arial, sans-serif';

        const d = new Date(data.date);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const serialOnlyDigits = String(data.quoteNumber || '1').replace(/\D/g, '');
        const serialPadded = String(serialOnlyDigits ? Number(serialOnlyDigits) : 1).padStart(3, '0');

        const projectLoc = String(location || '').replace(/\s+/g, '');
        const serial = (() => {
            const digits = String(data.quoteNumber || '').replace(/\D/g, '');
            const last3 = digits ? digits.slice(-3) : '';
            return last3 ? last3.padStart(3, '0') : '001';
        })();
        const formattedIdHtml = `<span style=\"color:#d00\">${projectLoc}</span>${yyyy}${mm}${dd}${serial}`;
        const headHtml = `
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;align-items:center;margin-top:18px;direction:ltr">
        <div style="text-align:left"></div>
        <div style="text-align:center"></div>
        <div></div>
      </div>
      <div style="border:1px solid #e0e0e0;border-radius:8px;padding:10px;margin-top:79px;background:rgba(255,255,255,0.9);width:86%;margin-left:auto;margin-right:auto">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:12px">
          <div>اسم العميل: ${customerName || '-'}</div>
          <div>الموقع: ${location || '-'}</div>
          <div>رقم الجوال: ${mobile || '-'}</div>
          <div style="direction:rtl;unicode-bidi:embed">${formattedIdHtml}</div>
        </div>
      </div>
    `;
        const bgImgHtml = `<img src="/QSREPORT.png.png" crossorigin="anonymous" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;z-index:-1" />`;
        const fontCss = `<style>@font-face{font-family:'Cairo';src:url('/fonts/Cairo-Regular.ttf') format('truetype');font-weight:normal;font-style:normal}</style>`;
        const agreedHp = data.quotes.length === 1
            ? (data.quotes[0].horsepower ?? 0)
            : ((data.quotes.find(q => q.category === 'الأفضل')?.horsepower) ?? (data.quotes[0].horsepower ?? 0));
        const agreedType = data.quotes.length === 1
            ? (data.quotes[0].deviceType)
            : ((data.quotes.find(q => q.category === 'الأفضل')?.deviceType) ?? (data.quotes[0].deviceType));
        const agreedCount = data.quotes.length === 1
            ? (data.quotes[0].deviceCount)
            : ((data.quotes.find(q => q.category === 'الأفضل')?.deviceCount) ?? (data.quotes[0].deviceCount));
        const introHtml = `
      <div style="margin-top:32px;font-size:12px;line-height:1.6;font-weight:700;margin-right:64px;width:88%">
        <div style="margin-right:12px">المكرم/ ${customerName || '-'}</div>
        <div style="margin-right:12px">تحية طيبة.</div>
        <div>بناء على الاجتماع الذي عقد معكم لمناقشة آلية تشغيل عدد (<span style="color:#e57373">${Number(agreedCount || 0)}</span>) ${agreedType} بقدرة <span style="color:#e57373">${Number(agreedHp || 0).toFixed(0)}</span> حصان على الطاقة الشمسية، نرفق لكم عرض السعر يتضمن الجوانب الفنية والتشغيلية</div>
      </div>
    `;

        const tableSingle = (q: ComputedQuote) => `
      <table style="width:100%;border-collapse:collapse;margin-top:160px;font-size:12px;background:#fff">
        <thead>
          <tr style="background:#f79156;color:#fff">
            <th style="padding:8px;text-align:center">البيان</th>
            <th style="padding:8px;text-align:center">القيمة</th>
            <th style="padding:8px;text-align:center"></th>
          </tr>
        </thead>
        <tbody>
          <tr style="background:#fff"><td style="padding:8px;text-align:right">عدد الأحصنة (HP)</td><td style="padding:8px;text-align:center">${Number(q.horsepower ?? 0).toFixed(0)}</td><td></td></tr>
          <tr style="background:#f9f9f9"><td style="padding:8px;text-align:right">القدرة (kW)</td><td style="padding:8px;text-align:center">${Number(q.capacityKw ?? 0).toFixed(0)}</td><td></td></tr>
          <tr style="background:#fff"><td style="padding:8px;text-align:right">الإجمالي قبل الضريبة</td><td style="padding:8px;text-align:center">${q.totalBeforeTax} ر.س</td><td></td></tr>
          <tr style="background:#f9f9f9"><td style="padding:8px;text-align:right">ضريبة القيمة المضافة (15%)</td><td style="padding:8px;text-align:center">${q.vat15} ر.س</td><td></td></tr>
          <tr style="background:#fff"><td style="padding:8px;text-align:right">الإجمالي شامل الضريبة</td><td style="padding:8px;text-align:center">${q.totalWithTax} ر.س</td><td></td></tr>
        </tbody>
        <tfoot>
          <tr><td style="padding:8px;text-align:right">عدد المضخات المراد تشغيلها على الطاقة الشمسية</td><td></td><td style="padding:8px;text-align:center">${q.deviceCount}</td></tr>
          <tr><td style="padding:8px;text-align:right">المحول</td><td></td><td style="padding:8px;text-align:center">${q.voltage}V</td></tr>
        </tfoot>
      </table>
      `;

        const tableMulti = (arr: ComputedQuote[]) => `
      <table style="width:100%;border-collapse:collapse;margin-top:210px;font-size:12px;background:#fff">
        <thead>
          <tr style="background:#f79156;color:#fff">
            <th style="padding:8px;text-align:center">الفئة</th>
            <th style="padding:8px;text-align:center">HP</th>
            <th style="padding:8px;text-align:center">kW</th>
            <th style="padding:8px;text-align:center">الإجمالي قبل الضريبة</th>
            <th style="padding:8px;text-align:center">الإجمالي شامل الضريبة</th>
            <th style="padding:8px;text-align:center"></th>
          </tr>
        </thead>
        <tbody>
          ${arr.map((q, idx) => `
            <tr style="background:${idx % 2 ? '#f9f9f9' : '#fff'}">
              <td style="padding:8px;text-align:center">${q.category}</td>
              <td style="padding:8px;text-align:center">${Number(q.horsepower ?? 0).toFixed(0)}</td>
              <td style="padding:8px;text-align:center">${Number(q.capacityKw ?? 0).toFixed(0)}</td>
              <td style="padding:8px;text-align:center">${q.totalBeforeTax} ر.س</td>
              <td style="padding:8px;text-align:center">${q.totalWithTax} ر.س</td>
              <td></td>
            </tr>
          `).join('')}
        </tbody>
        <tfoot>
          <tr><td colspan="5" style="padding:8px;text-align:right">عدد المضخات المراد تشغيلها على الطاقة الشمسية</td><td style="padding:8px;text-align:center">${arr[0]?.deviceCount ?? 1}</td></tr>
          <tr><td colspan="5" style="padding:8px;text-align:right">المحول</td><td style="padding:8px;text-align:center">${arr[0]?.voltage ?? 380}V</td></tr>
        </tfoot>
      </table>
    `;

        const paymentsHtml = ``;
        const notesHtml = ``;

        container.innerHTML = fontCss + bgImgHtml + headHtml + introHtml + (data.quotes.length === 1 ? tableSingle(data.quotes[0]) : tableMulti(data.quotes)) + paymentsHtml + notesHtml;
        document.body.appendChild(container);
        const canvas = await html2canvas(container, { scale: 2, useCORS: true });
        const dataUrl = canvas.toDataURL('image/png');
        document.body.removeChild(container);

        doc.addImage(dataUrl, 'PNG', 0, 0, pageWidth, pageHeight);
        doc.save(`Quotation-${projectLoc}${yyyy}${mm}${dd}${serial}.pdf`);
    };

    const handleSave = async () => {
        if (!customerName || quotes.length < 1) {
            toast.error('يرجى إدخال اسم العميل وإضافة عرض واحد على الأقل.');
            return;
        }
        setSaving(true);
        try {
            const canPost = !PREVIEW_OFFLINE;
            const now = new Date();
            const yyyy = now.getFullYear();
            const mm = String(now.getMonth() + 1).padStart(2, '0');
            const dd = String(now.getDate()).padStart(2, '0');
            let localQuoteNumber = `${yyyy}${mm}${dd}001`;
            if (canPost) {
                try {
                    const sres = await fetch(`${API_BASE_URL}/serial/today`);
                    if (sres.ok) {
                        const data = await sres.json();
                        if (data && data.serial) localQuoteNumber = String(data.serial);
                    }
                } catch { }
            }
            const itemsForPost = computedQuotes.map(q => ({
                category: q.category,
                horsepower: q.horsepower,
                capacity_kw: q.capacityKw,
                price_per_kw: q.pricePerKw,
                total_before_tax: q.totalBeforeTax,
                vat15: q.vat15,
                total_with_tax: q.totalWithTax,
                device_type: q.deviceType,
                device_count: q.deviceCount,
                voltage: q.voltage,
            }));
            if (canPost) {
                const res = await fetch(`${API_BASE_URL}/quotations`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        quote_number: localQuoteNumber,
                        quote_date: `${yyyy}-${mm}-${dd}`,
                        customer_name: customerName,
                        location,
                        mobile,
                        items: itemsForPost,
                    }),
                });
                if (!res.ok) throw new Error(await res.text());
                const data = await res.json();
                setQuoteNumber(String(data.quote_number || localQuoteNumber));
                setQuoteDate(String(data.quote_date || now.toISOString()));
                setSavedQuotations(prev => [{
                    id: String(data.id),
                    quote_number: String(data.quote_number),
                    quote_date: String(data.quote_date),
                    customer_name: String(data.customer_name),
                    location: data.location ?? null,
                    mobile: data.mobile ?? null,
                    created_at: String(data.created_at || new Date().toISOString()),
                    total_with_tax: Number(data.total_with_tax || computedQuotes.reduce((sum, q) => sum + Number(q.totalWithTax || 0), 0)),
                }, ...prev]);
            } else {
                const now = new Date();
                const yyyy = now.getFullYear();
                const mm = String(now.getMonth() + 1).padStart(2, '0');
                const dd = String(now.getDate()).padStart(2, '0');
                const lsKey = 'quotationSerialDaily';
                let serial = 1;
                try {
                    const raw = localStorage.getItem(lsKey);
                    const parsed = raw ? JSON.parse(raw) : null;
                    if (parsed && parsed.date === `${yyyy}-${mm}-${dd}`) {
                        serial = Number(parsed.serial || 0) + 1;
                    }
                    localStorage.setItem(lsKey, JSON.stringify({ date: `${yyyy}-${mm}-${dd}`, serial }));
                } catch { }
                const serialPadded = String(serial).padStart(3, '0');
                setQuoteNumber(localQuoteNumber);
                setQuoteDate(now.toISOString());
                const total = computedQuotes.reduce((sum, q) => sum + Number(q.totalWithTax || 0), 0);
                setSavedQuotations(prev => [{
                    id: `${yyyy}${mm}${dd}${serialPadded}`,
                    quote_number: localQuoteNumber,
                    quote_date: now.toISOString(),
                    customer_name: customerName,
                    location: location || null,
                    mobile: mobile || null,
                    created_at: now.toISOString(),
                    total_with_tax: total,
                }, ...prev]);
            }
            toast.success('تم حفظ العرض بنجاح');
        } catch (err: any) {
            const now = new Date();
            const fallbackNumber = `${now.toISOString().slice(2, 10).replace(/-/g, '')}${String(1).padStart(3, '0')}`;
            setQuoteNumber(fallbackNumber);
            setQuoteDate(now.toISOString());
            const total = computedQuotes.reduce((sum, q) => sum + Number(q.totalWithTax || 0), 0);
            setSavedQuotations(prev => [{
                id: fallbackNumber,
                quote_number: fallbackNumber,
                quote_date: now.toISOString(),
                customer_name: customerName,
                location: location || null,
                mobile: mobile || null,
                created_at: now.toISOString(),
                total_with_tax: total,
            }, ...prev]);
            toast.error('حدث خطأ أثناء الحفظ، تم الحفظ محلياً');
        } finally {
            setSaving(false);
        }
    };

    const handleExport = async () => {
        if (!customerName || quotes.length < 1) {
            toast.error('يرجى إدخال اسم العميل وإضافة عرض واحد على الأقل.');
            return;
        }
        setSaving(true);
        try {
            const number = quoteNumber;
            const dateIso = quoteDate || new Date().toISOString();
            if (!number) {
                const now = new Date(dateIso);
                const yyyy = now.getFullYear();
                const mm = String(now.getMonth() + 1).padStart(2, '0');
                const dd = String(now.getDate()).padStart(2, '0');
                const lsKey = 'quotationSerialDaily';
                let serial = 1;
                try {
                    const raw = localStorage.getItem(lsKey);
                    const parsed = raw ? JSON.parse(raw) : null;
                    if (parsed && parsed.date === `${yyyy}-${mm}-${dd}`) {
                        serial = Number(parsed.serial || 0) + 1;
                    }
                    localStorage.setItem(lsKey, JSON.stringify({ date: `${yyyy}-${mm}-${dd}`, serial }));
                } catch { }
                const serialPadded = String(serial).padStart(3, '0');
                const localQuoteNumber = `${yyyy}${mm}${dd}${serialPadded}`;
                setQuoteNumber(localQuoteNumber);
                setQuoteDate(dateIso);
                await generatePDF({ quoteNumber: localQuoteNumber, date: dateIso, quotes: computedQuotes });
            } else {
                await generatePDF({ quoteNumber: number, date: dateIso, quotes: computedQuotes });
            }
            toast.success('تم تصدير العرض بنجاح');
        } finally {
            setSaving(false);
        }
    };

    const PreviewTable: React.FC<{ computed: ComputedQuote[] }> = ({ computed }) => (
        <div className="overflow-x-auto rounded-xl border border-white/10">
            {computed.length === 1 ? (
                <table className="w-full text-sm text-center text-slate-300">
                    <thead className="bg-slate-800/50 text-slate-400">
                        <tr>
                            <th className="p-3 text-right">البيان</th>
                            <th className="p-3">القيمة</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        <tr><td className="p-3 text-right bg-slate-800/30">الفئة</td><td className="p-3">{computed[0].category}</td></tr>
                        <tr><td className="p-3 text-right bg-slate-800/30">النوع</td><td className="p-3">{computed[0].deviceType}</td></tr>
                        <tr><td className="p-3 text-right bg-slate-800/30">العدد</td><td className="p-3">{computed[0].deviceCount}</td></tr>
                        <tr><td className="p-3 text-right bg-slate-800/30">عدد الأحصنة (HP)</td><td className="p-3">{computed[0].horsepower ?? 0}</td></tr>
                        <tr><td className="p-3 text-right bg-slate-800/30">القدرة (kW)</td><td className="p-3">{computed[0].capacityKw}</td></tr>

                        <tr><td className="p-3 text-right bg-slate-800/30">الإجمالي قبل الضريبة</td><td className="p-3">{computed[0].totalBeforeTax} ر.س</td></tr>
                        <tr><td className="p-3 text-right bg-slate-800/30">ضريبة القيمة المضافة (15%)</td><td className="p-3">{computed[0].vat15} ر.س</td></tr>
                        <tr className="font-bold bg-emerald-500/10 text-emerald-400"><td className="p-3 text-right">الإجمالي شامل الضريبة</td><td className="p-3">{computed[0].totalWithTax} ر.س</td></tr>
                    </tbody>
                </table>
            ) : (
                <table className="w-full text-sm text-center text-slate-300">
                    <thead className="bg-slate-800/50 text-slate-400">
                        <tr>
                            <th className="p-3">الفئة</th>
                            <th className="p-3">النوع</th>
                            <th className="p-3">العدد</th>
                            <th className="p-3">عدد الأحصنة (HP)</th>
                            <th className="p-3">القدرة (kW)</th>
                            <th className="p-3">الإجمالي قبل الضريبة</th>
                            <th className="p-3">الإجمالي شامل الضريبة</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {computed.map((q, i) => (
                            <tr key={i} className="hover:bg-white/5 transition-colors">
                                <td className="p-3">{q.category}</td>
                                <td className="p-3">{q.deviceType}</td>
                                <td className="p-3">{q.deviceCount}</td>
                                <td className="p-3">{q.horsepower ?? 0}</td>
                                <td className="p-3">{q.capacityKw}</td>
                                <td className="p-3">{q.totalBeforeTax} ر.س</td>
                                <td className="p-3 font-bold text-emerald-400">{q.totalWithTax} ر.س</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="bg-slate-800/30 font-medium">
                            <td colSpan={6} className="p-3 text-right">عدد المضخات المراد تشغيلها على الطاقة الشمسية</td>
                            <td className="p-3 text-center">{computed[0]?.deviceCount ?? 1}</td>
                        </tr>
                        <tr className="bg-slate-800/30 font-medium">
                            <td colSpan={6} className="p-3 text-right">المحول</td>
                            <td className="p-3 text-center">{computed[0]?.voltage ?? 380}V</td>
                        </tr>
                    </tfoot>
                </table>
            )}
        </div>
    );

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text mb-2">إنشاء عرض سعر</h1>
                    <p className="text-slate-400">إعداد عروض أسعار أنظمة الطاقة الشمسية</p>
                </div>
                <button
                    onClick={() => navigate('/projects')}
                    className="px-4 py-2 rounded-xl glass-card hover:bg-white/10 text-slate-300 transition-all flex items-center gap-2"
                >
                    <ArrowRight size={18} />
                    <span>رجوع</span>
                </button>
            </div>

            {/* Customer Details */}
            <div className="glass-card p-6 rounded-2xl">
                <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                    <User className="text-blue-400" size={24} />
                    بيانات العميل
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-slate-400 mb-2">اسم العميل</label>
                        <div className="relative">
                            <User className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                            <input
                                type="text"
                                value={customerName}
                                onChange={e => setCustomerName(e.target.value)}
                                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-3 pr-10 pl-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                placeholder="الاسم الكامل"
                                required
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-400 mb-2">الموقع</label>
                        <div className="relative">
                            <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                            <input
                                type="text"
                                value={location}
                                onChange={e => setLocation(e.target.value)}
                                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-3 pr-10 pl-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                placeholder="المدينة / المنطقة"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-400 mb-2">رقم الجوال</label>
                        <div className="relative">
                            <Phone className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                            <input
                                type="text"
                                value={mobile}
                                onChange={e => setMobile(e.target.value)}
                                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-3 pr-10 pl-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                placeholder="05xxxxxxxx"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Quotes */}
            <div className="glass-card p-6 rounded-2xl">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <FileText className="text-purple-400" size={24} />
                        العروض
                    </h2>
                    <button
                        onClick={addQuote}
                        disabled={quotes.length >= 4}
                        className="px-4 py-2 rounded-lg bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm font-medium"
                    >
                        <Plus size={16} />
                        إضافة عرض
                    </button>
                </div>

                <div className="space-y-6">
                    {quotes.map((q, idx) => (
                        <div key={idx} className="bg-slate-800/30 rounded-xl p-4 border border-white/5 relative group">
                            <div className="grid grid-cols-1 md:grid-cols-7 gap-4 items-end">
                                <div className="md:col-span-1">
                                    <label className="block text-xs font-medium text-slate-400 mb-1">الفئة</label>
                                    <div className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg py-2.5 px-3 text-slate-300 text-sm">
                                        {categoryByIndex(idx)}
                                    </div>
                                </div>
                                <div className="md:col-span-1">
                                    <label className="block text-xs font-medium text-slate-400 mb-1">عدد الأحصنة (HP)</label>
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        min="0"
                                        value={q.horsepowerInput}
                                        placeholder="مثال: 10"
                                        onChange={e => updateQuote(idx, 'horsepowerInput', e.target.value)}
                                        className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg py-2.5 px-3 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="block text-xs font-medium text-slate-400 mb-1">النوع</label>
                                    <select
                                        className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg py-2.5 px-3 text-white focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm appearance-none"
                                        value={q.deviceTypeInput}
                                        onChange={e => updateQuote(idx, 'deviceTypeInput', e.target.value)}
                                    >
                                        <option value="رأس كهربائي">رأس كهربائي</option>
                                        <option value="غطاس">غطاس</option>
                                    </select>
                                </div>
                                <div className="md:col-span-1">
                                    <label className="block text-xs font-medium text-slate-400 mb-1">عدد المضخات</label>
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        min="1"
                                        value={q.deviceCountInput}
                                        placeholder="مثال: 1"
                                        onChange={e => updateQuote(idx, 'deviceCountInput', e.target.value)}
                                        className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg py-2.5 px-3 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="block text-xs font-medium text-slate-400 mb-1">القدرة (kW)</label>
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        min="0"
                                        value={q.capacityKwInput}
                                        placeholder="مثال: 7.5"
                                        onChange={e => updateQuote(idx, 'capacityKwInput', e.target.value)}
                                        className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg py-2.5 px-3 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="block text-xs font-medium text-slate-400 mb-1">السعر/kW (1100)</label>
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        min="0"
                                        value={q.pricePerKwInput}
                                        placeholder="1100"
                                        onChange={e => updateQuote(idx, 'pricePerKwInput', e.target.value)}
                                        className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg py-2.5 px-3 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="block text-xs font-medium text-slate-400 mb-1">المحول</label>
                                    <div className="w-full bg-slate-900/50 border border-slate-700/50 rounded-lg py-2.5 px-3 text-slate-300 text-sm">
                                        380V
                                    </div>
                                </div>
                            </div>

                            {quotes.length > 1 && (
                                <button
                                    onClick={() => removeQuote(idx)}
                                    className="absolute -top-2 -left-2 p-1.5 bg-red-500/10 text-red-400 rounded-full hover:bg-red-500 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                                >
                                    <Trash2 size={14} />
                                </button>
                            )}
                        </div>
                    ))}

                    <div className="flex justify-end gap-3 pt-4 border-t border-white/5">
                        <button
                            onClick={handleSave}
                            disabled={saving || !customerName}
                            className="px-6 py-2.5 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-emerald-500/20 flex items-center gap-2"
                        >
                            {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <SaveIcon size={18} />}
                            <span>حفظ</span>
                        </button>
                        <button
                            onClick={handleExport}
                            disabled={saving || !customerName}
                            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white font-medium hover:from-blue-500 hover:to-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/20 flex items-center gap-2"
                        >
                            {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <FileDown size={18} />}
                            <span>تصدير PDF</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Saved Quotations */}
            {savedQuotations.length > 0 && (
                <div className="glass-card p-6 rounded-2xl">
                    <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                        <Briefcase className="text-amber-400" size={24} />
                        العروض المحفوظة
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {savedQuotations.map(item => (
                            <div
                                key={item.id}
                                className="bg-slate-800/30 border border-white/5 rounded-xl p-4 hover:bg-slate-800/50 transition-all cursor-pointer group relative overflow-hidden"
                                onClick={() => navigate(`/quotations/${item.id}`)}
                            >
                                <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />

                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <h3 className="font-bold text-white truncate max-w-[180px]">{item.customer_name}</h3>
                                        <p className="text-xs text-slate-400">{item.location || '-'}</p>
                                    </div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); deleteQuotation(item.id); }}
                                        className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>

                                <div className="space-y-2 text-xs text-slate-300">
                                    <div className="flex justify-between">
                                        <span className="text-slate-500 flex items-center gap-1"><Zap size={12} /> HP</span>
                                        <span className="font-medium">{quotationDetails[item.id]?.horsepower ?? '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500 flex items-center gap-1"><Calendar size={12} /> التاريخ</span>
                                        <span className="font-medium">{new Date(item.quote_date).toLocaleDateString('en-GB')}</span>
                                    </div>
                                    <div className="pt-2 mt-2 border-t border-white/5 flex justify-between items-center">
                                        <span className="text-slate-400">الإجمالي</span>
                                        <span className="font-bold text-emerald-400 text-sm">{Number(item.total_with_tax || 0).toLocaleString('en-GB')} ر.س</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Preview */}
            <div className="glass-card p-6 rounded-2xl">
                <h2 className="text-xl font-bold text-white mb-6">تفاصيل العرض المالي</h2>
                <PreviewTable computed={computedQuotes} />
                {quoteNumber && quoteDate && (
                    <div className="mt-4 text-xs text-slate-500 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                        تم توليد رقم العرض: {quoteNumber} — التاريخ: {new Date(quoteDate).toLocaleDateString('ar-SA')}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ModernCreateQuotationScreen;
